import { oc } from "@orpc/contract";
import { z } from "zod";
import { DocumentAssociationType } from "@erp/shared-types";

// ============================================================================
// Get Upload URL - Step 1 of presigned upload flow
// ============================================================================

export const GetDocumentUploadUrlRequestSchema = z.object({
  entityType: z.string().min(1),
  entityId: z.uuid(),
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1),
});

export const GetDocumentUploadUrlResponseSchema = z.object({
  documentId: z.uuid(),
  uploadUrl: z.string().url(),
});

// ============================================================================
// Confirm Document Upload - Step 2 of presigned upload flow
// ============================================================================

export const ConfirmDocumentUploadRequestSchema = z.object({
  documentId: z.uuid(),
  entityType: z.string().min(1),
  entityId: z.uuid(),
  associationType: z.nativeEnum(DocumentAssociationType).optional(),
  fileSizeBytes: z.number().int().min(0).optional(),
});

export const DocumentSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /**
   * @deprecated Moved to the associated File row in Phase 8.
   * Retained for backwards compatibility; always "" on documents created
   * after the file/document separation migration.
   */
  storagePath: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
  /**
   * @deprecated Moved to the associated File row in Phase 8.
   * Retained for backwards compatibility; always "" on documents created
   * after the file/document separation migration.
   */
  status: z.string(),
  /** Present for documents that have been assigned a File row (Phase 2+). */
  fileId: z.uuid().nullable().optional(),
});

export const ConfirmDocumentUploadResponseSchema = z.object({
  document: DocumentSchema,
});

// ============================================================================
// Get Viewer URL - Generate Office Online viewer URL for a document
// ============================================================================

export const GetDocumentViewerUrlRequestSchema = z.object({
  documentId: z.uuid(),
});

export const GetDocumentViewerUrlResponseSchema = z.object({
  viewerUrl: z.union([z.literal(""), z.string().url()]),
  /** Whether the file type is supported by Office Online viewer */
  supported: z.boolean(),
  /** The original file name */
  fileName: z.string(),
  /** MIME type of the document */
  mimeType: z.string().nullable(),
});

// ============================================================================
// List Documents For Picker
// ============================================================================

export const ListDocumentsForPickerRequestSchema = z.object({
  query: z.string().optional(),
  limit: z.number().int().min(1).max(50).optional(),
});

export const DocumentPickerItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  mimeType: z.string().nullable(),
  createdAt: z.date(),
});

export const ListDocumentsForPickerResponseSchema = z.object({
  documents: z.array(DocumentPickerItemSchema),
});

// ============================================================================
// Get Document Preview - Signed URL for inline file viewing
// ============================================================================

export const GetDocumentPreviewRequestSchema = z.object({
  documentId: z.uuid(),
});

export const GetDocumentPreviewResponseSchema = z.object({
  fileName: z.string(),
  mimeType: z.string().nullable(),
  signedUrl: z.string(),
  /** Present for documents that have been assigned a File row (Phase 2+). */
  fileId: z.uuid().nullable().optional(),
  metadata: z.object({
    classificationType: z.string().nullable(),
    classificationMethod: z.string().nullable(),
    classificationConfidence: z.number().nullable(),
    classifiedAt: z.date().nullable(),
    parentDocumentId: z.uuid().nullable(),
    childDocumentsCount: z.number().int().nonnegative(),
    isSplitParent: z.boolean(),
    isSplitChild: z.boolean(),
    isTransformedFile: z.boolean(),
  }),
});

// ============================================================================
// Get OCR Result Preview - Lightweight OCR result details for hover previews
// ============================================================================

export const GetDocumentOcrResultPreviewRequestSchema = z.object({
  ocrResultId: z.uuid(),
});

export const GetDocumentOcrResultPreviewResponseSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
  status: z.string(),
  vendor: z.string(),
  modelVersion: z.string(),
  error: z.string().nullable(),
  createdAt: z.date(),
  extractedTextPreview: z.string().nullable(),
});

// ============================================================================
// Get OCR Result Content - Full OCR payload for dedicated view
// ============================================================================

export const GetDocumentOcrResultContentRequestSchema = z.object({
  ocrResultId: z.uuid(),
});

export const GetDocumentOcrResultContentResponseSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
  fullResult: z.unknown().nullable(),
});

// ============================================================================
// Remove Document - Remove document association from an entity
// ============================================================================

export const RemoveDocumentRequestSchema = z.object({
  documentId: z.uuid(),
  entityType: z.string().min(1),
  entityId: z.uuid(),
});

export const RemoveDocumentResponseSchema = z.object({
  removed: z.boolean(),
  documentDeleted: z.boolean(),
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const documentContracts = {
  getUploadUrl: oc
    .input(GetDocumentUploadUrlRequestSchema)
    .output(GetDocumentUploadUrlResponseSchema)
    .route({
      method: "POST",
      summary: "Start a document upload for a record",
      description:
        "Creates a pending document attached to a given record and returns a temporary URL the browser uploads the file to.",
    }),

  confirmUpload: oc
    .input(ConfirmDocumentUploadRequestSchema)
    .output(ConfirmDocumentUploadResponseSchema)
    .route({
      method: "POST",
      summary: "Confirm a document upload",
      description:
        "Marks an uploaded document as complete and attaches it to the given record, as the original file unless another association type is given.",
    }),

  getViewerUrl: oc
    .input(GetDocumentViewerUrlRequestSchema)
    .output(GetDocumentViewerUrlResponseSchema)
    .route({
      method: "POST",
      summary: "Get an online viewer link for a document",
      description:
        "Returns an Office Online viewer URL for Word, Excel or PowerPoint documents; reports unsupported for other file types or when the viewer is disabled.",
    }),

  getPreview: oc
    .input(GetDocumentPreviewRequestSchema)
    .output(GetDocumentPreviewResponseSchema)
    .route({
      method: "POST",
      summary: "Get a document preview",
      description:
        "Returns a temporary file link plus classification details and split-document relationships (parent, child count) for one document.",
    }),

  getOcrResultPreview: oc
    .input(GetDocumentOcrResultPreviewRequestSchema)
    .output(GetDocumentOcrResultPreviewResponseSchema)
    .route({
      method: "POST",
      summary: "Get a text extraction result summary",
      description:
        "Returns an OCR result's status, provider, model version, error, and a short preview of the extracted text.",
    }),

  getOcrResultContent: oc
    .input(GetDocumentOcrResultContentRequestSchema)
    .output(GetDocumentOcrResultContentResponseSchema)
    .route({
      method: "POST",
      summary: "Get full text extraction result",
      description:
        "Returns the complete raw OCR output for one extraction result of a document.",
    }),

  listForPicker: oc
    .input(ListDocumentsForPickerRequestSchema)
    .output(ListDocumentsForPickerResponseSchema)
    .route({
      method: "GET",
      summary: "List my uploaded documents",
      description:
        "Returns the signed-in user's most recent documents (up to 50), optionally filtered by file name, for choosing a file to attach.",
    }),

  remove: oc
    .input(RemoveDocumentRequestSchema)
    .output(RemoveDocumentResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a document from a record",
      description:
        "Detaches a document from the given record; deletes the file from storage when no other record still references it.",
    }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type GetDocumentUploadUrlRequest = z.infer<
  typeof GetDocumentUploadUrlRequestSchema
>;
export type GetDocumentUploadUrlResponse = z.infer<
  typeof GetDocumentUploadUrlResponseSchema
>;
export type ConfirmDocumentUploadRequest = z.infer<
  typeof ConfirmDocumentUploadRequestSchema
>;
export type ConfirmDocumentUploadResponse = z.infer<
  typeof ConfirmDocumentUploadResponseSchema
>;
export type GetDocumentViewerUrlRequest = z.infer<
  typeof GetDocumentViewerUrlRequestSchema
>;
export type GetDocumentViewerUrlResponse = z.infer<
  typeof GetDocumentViewerUrlResponseSchema
>;
export type GetDocumentPreviewRequest = z.infer<
  typeof GetDocumentPreviewRequestSchema
>;
export type GetDocumentPreviewResponse = z.infer<
  typeof GetDocumentPreviewResponseSchema
>;
export type GetDocumentOcrResultPreviewRequest = z.infer<
  typeof GetDocumentOcrResultPreviewRequestSchema
>;
export type GetDocumentOcrResultPreviewResponse = z.infer<
  typeof GetDocumentOcrResultPreviewResponseSchema
>;
export type GetDocumentOcrResultContentRequest = z.infer<
  typeof GetDocumentOcrResultContentRequestSchema
>;
export type GetDocumentOcrResultContentResponse = z.infer<
  typeof GetDocumentOcrResultContentResponseSchema
>;
export type RemoveDocumentRequest = z.infer<typeof RemoveDocumentRequestSchema>;
export type RemoveDocumentResponse = z.infer<
  typeof RemoveDocumentResponseSchema
>;
export type Document = z.infer<typeof DocumentSchema>;
export type ListDocumentsForPickerRequest = z.infer<
  typeof ListDocumentsForPickerRequestSchema
>;
export type ListDocumentsForPickerResponse = z.infer<
  typeof ListDocumentsForPickerResponseSchema
>;
export type DocumentPickerItem = z.infer<typeof DocumentPickerItemSchema>;
